<template>
    <q-page class="planificacion-page">
        <!-- Modern Header -->
        <q-card class="planificacion-header q-mb-sm">
            <q-card-section class="header-content q-py-sm">
                <div class="title-section">
                    <q-avatar size="36px" color="primary" text-color="white" class="q-mr-sm">
                        <q-icon name="account_tree" size="20px" />
                    </q-avatar>
                    <div>
                        <div class="text-h6">Planificación Gantt</div>
                        <div class="text-caption text-white">Gestión y visualización de tareas de producción</div>
                    </div>
                </div>
            </q-card-section>
        </q-card>

        <!-- Modern Controls Card -->
        <q-card class="planificacion-controls q-mb-md">
            <q-card-section class="controls-toolbar">
                <div class="toolbar-left">
                    <GltnSelectMaquina v-model="selectedMachine" label="Máquina" class="machine-select q-mr-sm" emit-value outlined dense />
                    <GltnInputFecha v-model="start" label="Desde" class="date-input q-mr-sm" outlined dense />
                    <GltnInputFecha v-model="end" label="Hasta" class="date-input q-mr-sm" outlined dense />

                    <q-btn unelevated color="primary" icon="refresh" label="Actualizar" @click="refreshGantt" :loading="loading" class="action-btn q-mr-sm">
                        <q-tooltip>Recargar datos</q-tooltip>
                    </q-btn>
                    <q-btn-group unelevated class="zoom-controls">
                        <q-btn color="secondary" icon="zoom_in" @click="zoomIn" class="zoom-btn">
                            <q-tooltip>Acercar</q-tooltip>
                        </q-btn>
                        <q-btn color="secondary" icon="zoom_out" @click="zoomOut" class="zoom-btn">
                            <q-tooltip>Alejar</q-tooltip>
                        </q-btn>
                    </q-btn-group>
                </div>
                <div class="toolbar-right">
                    <GltnInput v-model="searchText" label="Buscar tareas" placeholder="Buscar tareas, productos u órdenes..." class="search-input" outlined dense clearable debounce="300" @update:model-value="filterGantt">
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </GltnInput>
                </div>
            </q-card-section>
            <q-card-section class="controls-toolbar">
                <q-btn color="deep-purple" icon="view_column" @click="columnasToggle" label="Columnas" class="q-mr-sm" unelevated>
                    <q-tooltip>Mostrar/ocultar columnas</q-tooltip>
                </q-btn>
                <q-btn color="teal" icon="unfold_more" @click="expandAll" label="Expandir" class="q-mr-sm" unelevated>
                    <q-tooltip>Expandir todas las ramas</q-tooltip>
                </q-btn>
                <q-btn color="orange" text-color="white" icon="unfold_less" @click="collapseAll" label="Contraer" class="q-mr-sm" unelevated>
                    <q-tooltip>Contraer todas las ramas</q-tooltip>
                </q-btn>
                <q-btn :color="isFullscreen ? 'red' : 'indigo'" :icon="isFullscreen ? 'fullscreen_exit' : 'fullscreen'" @click="toggleFullscreen" :label="isFullscreen ? 'Restaurar' : 'Maximizar'" unelevated>
                    <q-tooltip>{{ isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa' }}</q-tooltip>
                </q-btn>
            </q-card-section>
        </q-card>

        <!-- Gantt Chart Card -->
        <q-card class="planificacion-gantt-card">
            <q-card-section class="gantt-container-section">
                <div ref="ganttContainer" class="gantt-chart"></div>
            </q-card-section>
            <q-inner-loading :showing="loading">
                <q-spinner-hourglass size="50px" color="primary" />
            </q-inner-loading>
        </q-card>
    </q-page>
</template>

<script setup>
import "@dhx/gantt/codebase/dhtmlxgantt.css";
import GltnInput from "@/components/form/GltnInput.vue";
import GltnInputFecha from "@/components/form/GltnInputFecha.vue";
import GltnSelectMaquina from "@/components/form/GltnSelectMaquina.vue";
import PlanificacionService from "@/services/PlanificacionService.js";
import { desdeMicrosiga } from "@/composables/useMicroDateParser.js";
import { gantt } from "@dhx/gantt";
import { onMounted, onUnmounted, ref } from "vue";
import { useDefaultDataStore } from "@/stores/useDefaultDataStore.js";
import numberFormat from "@/helpers/numberFormat.js";
import { useQuasar } from "quasar";

const defaultData = useDefaultDataStore();
const $q = useQuasar();
const ganttContainer = ref(null);
const loading = ref(false);
const isFullscreen = ref(false);
const searchText = ref(null);
const columnsSelected = ref([]);
const columnsOptions = ref([]);
const selectedMachine = ref(defaultData.getPlanificacionGanttMachine || null);
const start = ref(defaultData.getPlanificacionGanttStart || null);
const end = ref(defaultData.getPlanificacionGanttEnd || null);
const zoomConfig = {
    minColumnWidth: 20,
    maxColumnWidth: 500,
    levels: [
        {
            name: "hour",
            scale_height: 27,
            min_column_width: 20,
            scales: [
                { unit: "day", format: "%d" },
                { unit: "hour", format: "%H" },
            ],
        },
        {
            name: "day",
            scale_height: 27,
            min_column_width: 80,
            scales: [{ unit: "day", step: 1, format: "%d %M" }],
        },
        {
            name: "week",
            scale_height: 50,
            min_column_width: 50,
            scales: [
                {
                    unit: "week",
                    step: 1,
                    format: function (date) {
                        const dateToStr = gantt.date.date_to_str("%d %M");
                        const endDate = gantt.date.add(date, -6, "day");
                        const weekNum = gantt.date.date_to_str("%W")(date);
                        return "#" + weekNum + ", " + dateToStr(date) + " - " + dateToStr(endDate);
                    },
                },
                { unit: "day", step: 1, format: "%j %D" },
            ],
        },
        {
            name: "month",
            scale_height: 50,
            min_column_width: 120,
            scales: [
                { unit: "month", format: "%F, %Y" },
                { unit: "week", format: "Week #%W" },
            ],
        },
        {
            name: "quarter",
            height: 50,
            min_column_width: 90,
            scales: [
                {
                    unit: "quarter",
                    step: 1,
                    format: function (date) {
                        const dateToStr = gantt.date.date_to_str("%M");
                        const endDate = gantt.date.add(gantt.date.add(date, 3, "month"), -1, "day");
                        return dateToStr(date) + " - " + dateToStr(endDate);
                    },
                },
                { unit: "month", step: 1, format: "%M" },
            ],
        },
        {
            name: "year",
            scale_height: 50,
            min_column_width: 30,
            scales: [{ unit: "year", step: 1, format: "%Y" }],
        },
    ],
    useKey: "ctrlKey",
    trigger: "wheel",
    // element: function () {
    //     return gantt.$root.querySelector(".gantt_task");
    // }
};

const processTask = async (task) => {
    loading.value = true;
    try {
        const data = {
            SEMANA: task.datos.SEMANA,
            ORDEN: task.datos.ORDEN,
            C2_PRODUTO: task.datos.C2_PRODUTO,
            C2_FILIAL: task.datos.C2_FILIAL,
            C2_NUM: task.datos.C2_NUM,
            C2_ITEM: task.datos.C2_ITEM,
            C2_SEQUEN: task.datos.C2_SEQUEN,
            C2_DATPRI: task.datos.C2_DATPRI,
            C2_DATPRI_NEW: task.start_date,
            C2_PRIOR: task.prioridad,
            C2_DATPRF: gantt.calculateEndDate(task),
        };
        await PlanificacionService.fechasUpdate(data);
        return true;
    } catch (error) {
        return false;
    } finally {
        loading.value = false;
    }
};

// Función para obtener el color púrpura según el nivel (gradiente de claro a oscuro)
const getPurpleColor = (level) => {
    const colorMap = {
        0: "#e1bee7", // Muy claro (Semana)
        1: "#ce93d8", // Claro (Producto)
        2: "#ba68c8", // Medio (Espesor)
        3: "#ab47bc", // Oscuro (Color)
        4: "#4a148c", // Muy oscuro (Orden)
    };
    return colorMap[level] || "#5e35b1"; // fallback
};

onMounted(() => {
    document.addEventListener("fullscreenchange", onFullscreenChange);

    const libCredTemplate = function (task) {
        if (task.libCred === "SI") {
            return `<div style="color: green; font-weight: bold; display: flex; align-items: center; justify-content: center; gap: 6px;">✔</div>`;
        } else if (task.libCred === "NO") {
            return `<div style="color: red; font-weight: bold; display: flex; align-items: center; justify-content: center; gap: 6px;">❌</div>`;
        } else {
            return `<div>${task.libCred}</div>`;
        }
    };
    const libLogTemplate = function (task) {
        if (task.libLog === "SI") {
            return `<div style="color: green; font-weight: bold; display: flex; align-items: center; justify-content: center; gap: 6px;">✔</div>`;
        } else if (task.libLog === "NO") {
            return `<div style="color: red; font-weight: bold; display: flex; align-items: center; justify-content: center; gap: 6px;">❌</div>`;
        } else {
            return `<div>${task.libLog}</div>`;
        }
    };

    const reporte = function (task) {
        if (task.quje === 0) {
            return `<div style="color: red; font-weight: bold; display: flex; align-items: center; justify-content: center; gap: 6px;">❌</div>`;
        } else if (task.quje === task.cant) {
            return `<div style="color: green; font-weight: bold; display: flex; align-items: center; justify-content: center; gap: 6px;">✔</div>`;
        } else {
            return `<div style="color: orange; font-weight: bold; display: flex; align-items: center; justify-content: center; gap: 6px;">🟡</div>`;
        }
    };

    gantt.config = {
        ...gantt.config,
        columns: [
            {
                name: "text",
                label: "Semana",
                align: "left",
                min_width: 250,
                tree: true,
                hide: false,
                resize: true,
            },
            {
                name: "productoTipo",
                label: "Tipo",
                align: "center",
                width: "*",
                hide: true,
            },
            { name: "espesor", label: "Esp", align: "center", width: 60, hide: true },
            { name: "panelColor", label: "Color", align: "center", width: 60, hide: true },
            { name: "panelColor2", label: "Color2", align: "center", width: 60, hide: true },
            {
                name: "presupuesto",
                label: "Presup",
                align: "center",
                width: "*",
                min_width: 80,
                hide: false,
            },
            {
                name: "orden",
                label: "Orden",
                align: "center",
                width: "*",
                min_width: 120,
                hide: false,
            },
            {
                name: "producto",
                label: "Producto",
                align: "center",
                width: 150,
                min_width: 150,
                hide: false,
            },
            {
                name: "prioridad",
                label: "Prioridad",
                align: "center",
                width: "*",
                min_width: 120,
                hide: false,
            },
            {
                name: "cliente",
                label: "Cliente",
                align: "left",
                width: 150,
                min_width: 150,
                hide: false,
            },
            {
                name: "fEntrega",
                label: "F Entreg",
                align: "center",
                width: 150,
                min_width: 80,
                hide: false,
            },
            {
                name: "libCred",
                label: "Lib Cred",
                align: "center",
                width: 100,
                min_width: 60,
                template: libCredTemplate,
                hide: false,
            },
            {
                name: "libLog",
                label: "Lib Log",
                align: "center",
                width: 100,
                min_width: 60,
                template: libLogTemplate,
                hide: false,
            },
            {
                name: "reporte",
                label: "Reporte",
                align: "center",
                width: 100,
                min_width: 60,
                template: reporte,
                hide: false,
            },
            {
                name: "puertaSalida",
                label: "Puerta Salida",
                align: "center",
                width: 100,
                min_width: 60,
                hide: false,
            },
            {
                name: "tiempoLinea",
                label: "Tiempo",
                align: "center",
                width: 150,
                min_width: 60,
                hide: false,
            },
            {
                name: "start_date",
                label: "Inicio",
                align: "center",
                width: 100,
                min_width: 100,
                hide: false,
            },
            {
                name: "cant",
                label: "Cantidad",
                align: "center",
                width: 100,
                min_width: 100,
                hide: false,
            },
        ],
        grid_elastic_columns: "min_width",
        drag_timeline: true,
        drag_project: true,
        sort: true,
        server_utc: false,
        order_branch: "marker",
        layout: {
            css: "gantt_container",
            cols: [
                {
                    width: 600,
                    // minWidth: 200,
                    // maxWidth: 900,
                    rows: [
                        {
                            view: "grid",
                            scrollX: "gridScroll",
                            scrollable: true,
                            scrollY: "scrollVer",
                        },
                        { view: "scrollbar", id: "gridScroll", group: "horizontal" },
                    ],
                },
                { resizer: true, width: 1 },
                {
                    rows: [
                        { view: "timeline", scrollX: "scrollHor", scrollY: "scrollVer" },
                        // horizontal scrollbar for the timeline
                        { view: "scrollbar", id: "scrollHor", group: "horizontal" },
                    ],
                },
                { view: "scrollbar", id: "scrollVer" },
            ],
        },
        lightbox: {
            sections: [
                { name: "prioridad", height: 30, map_to: "prioridad", type: "textarea" },
            ],
        },
    };
    columnsOptions.value = gantt.config.columns.map((col) => ({
        label: col.label,
        value: col.name,
    }));
    columnsSelected.value = columnsOptions.value.map((col) => col.value);
    gantt.plugins({
        multiselect: true,
        tooltip: true,
        marker: true,
        grouping: true,
        zoom: true,
    });

    // Aplicar template de clase dinámica para colorear según nivel
    gantt.templates.task_class = function (start, end, task) {
        if (!task.taskLevel) return "";
        return `gantt_task_level_${task.taskLevel}`;
    };

    gantt.ext.zoom.init(zoomConfig);
    gantt.ext.zoom.setLevel("day");

    // Guardar estado original antes del cambio
    let originalTasks = [];

    gantt.attachEvent("onBeforeTaskChanged", function (id, mode, task) {
        originalTasks.push(task);
        return true;
    });

    gantt.attachEvent("onAfterTaskDrag", async function (id, mode, e) {
        const updatedTask = gantt.getTask(id);
        const originalTask = originalTasks.find((task) => task.id === id);
        const originalStart = originalTask ? new Date(originalTask.start_date).getTime() : null;
        const updatedStart = new Date(updatedTask.start_date).getTime();
        // Si la fecha no cambió, no hacemos nada
        if (originalStart === updatedStart) {
            return;
        }
        const result = await processTask(updatedTask);

        if (!result) {
            // Revertir cambios si falla
            if (originalTask) {
                gantt.updateTask(id, originalTask);
            }
            // Remove the original task from the array after reverting
            originalTasks = originalTasks.filter((task) => task.id !== id);
        } else {
            // Remove the original task from the array after successful update
            originalTasks = originalTasks.filter((task) => task.id !== id);
        }
    });

    gantt.attachEvent("onBeforeTaskDisplay", function (id, task) {
        if (!searchText.value) return true;

        const search = searchText.value.toLowerCase();
        const searchableFields = [task.text, task.orden, task.datos?.C2_PRODUTO].filter(Boolean);

        return searchableFields.some((field) => field.toLowerCase().includes(search));
    });

    gantt.attachEvent("onBeforeRowDragMove", function (id,parent,tindex) {
        const task = gantt.getTask(id);
        if(task.parent === parent) {
            return true; // Permitir reordenar dentro del mismo padre
        }
        return false; // Bloquear movimiento a otro padre
    });
    gantt.attachEvent("onLightboxSave", async function (id,task,is_new) {
        console.log("Guardando cambios en lightbox para tarea:", id, task);
        await processTask(task);
        return true;
    });
    gantt.init(ganttContainer.value);
});
const filterGantt = () => gantt.render();
const zoomIn = () => gantt.ext.zoom.zoomIn();
const zoomOut = () => gantt.ext.zoom.zoomOut();
const expandAll = () => {
    gantt.eachTask(function (task) {
        task.$open = true;
    });
    gantt.render();
};
const collapseAll = () => {
    gantt.eachTask(function (task) {
        task.$open = false;
    });
    gantt.render();
};
const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        isFullscreen.value = true;
    } else {
        document.exitFullscreen();
        isFullscreen.value = false;
    }
};
const onFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement;
};
onUnmounted(() => {
    document.removeEventListener("fullscreenchange", onFullscreenChange);
});
const columnasToggle = () => {
    $q.dialog({
        title: "Seleccionar Columnas",
        message: "Elija las columnas que desea mostrar en el Gantt:",
        options: {
            type: "checkbox",
            model: columnsSelected.value,
            // inline: true
            items: columnsOptions.value,
        },
        cancel: true,
        persistent: true,
    }).onOk((selected) => {
        columnsSelected.value = selected;
        aplicarColumnasSeleccionadas(selected);
    });
};
const aplicarColumnasSeleccionadas = (selectedColumns) => {
    gantt.config.columns.forEach((col) => {
        if (selectedColumns.includes(col.name)) {
            col.hide = false;
        } else {
            col.hide = true;
        }
    });
    gantt.render();
};

const refreshGantt = async () => {
    defaultData.setPlanificacionGantt({
        selectedMachine: selectedMachine.value,
        start: start.value,
        end: end.value,
    });
    loading.value = true;

    try {
        const response = await PlanificacionService.getTasks(selectedMachine.value, start.value, end.value);

        // Agrupar tareas por proyecto para cálculo eficiente de fechas
        const projectDateRanges = new Map();
        const tasksByProject = new Map();
        const taskSeenMap = new Map(); // Para detectar duplicados

        // Helper para construir ID de proyecto
        const buildProjectId = (semana, producto, espesor, color) => {
            return [semana, producto, espesor, color].filter(Boolean).join("-");
        };

        // Helper para determinar el padre según jerarquía y calcular el nivel
        const getParentIdWithLevel = (semana, producto, espesor, color) => {
            if (!semana) return { parentId: null, level: 0 };

            // Determinar el nivel según la estructura jerárquica
            let level = 0;
            let parentId = null;

            if (color && espesor && producto) {
                // Cuarto nivel (Color): el padre es SEMANA-PRODUCTO-ESPESOR
                parentId = buildProjectId(semana, producto, espesor);
                level = 3;
            } else if (espesor && producto) {
                // Tercer nivel (Espesor): el padre es SEMANA-PRODUCTO
                parentId = buildProjectId(semana, producto);
                level = 2;
            } else if (producto) {
                // Segundo nivel (Producto): el padre es SEMANA
                parentId = semana;
                level = 1;
            } else {
                // Primer nivel (Semana): no tiene padre
                level = 0;
            }

            return { parentId, level };
        };

        // Primera pasada: recolectar información y calcular rangos de fechas
        response.forEach((task) => {
            const isProject = !task.ORDEN;
            const projectId = buildProjectId(task.SEMANA, task.PRODUCTO, task.ESPESOR, task.COLOR);

            // Solo procesamos fechas de tareas reales (no proyectos)
            if (!isProject && task.C2_DATPRI) {
                const startDate = desdeMicrosiga(task.C2_DATPRI);

                if (!projectDateRanges.has(projectId)) {
                    projectDateRanges.set(projectId, {
                        minDate: startDate,
                        maxDate: startDate,
                    });
                } else {
                    const range = projectDateRanges.get(projectId);
                    if (startDate < range.minDate) range.minDate = startDate;
                    if (startDate > range.maxDate) range.maxDate = startDate;
                }

                // Agrupar tareas por proyecto para debugging
                if (!tasksByProject.has(projectId)) {
                    tasksByProject.set(projectId, []);
                }
                tasksByProject.get(projectId).push({
                    orden: task.ORDEN,
                    fecha: task.C2_DATPRI,
                    fechaParsed: startDate,
                    projectId,
                    semana: task.SEMANA,
                    producto: task.PRODUCTO,
                });
            }
        });

        // Segunda pasada: construir estructura final de tareas
        const tasks = response.map((task, index) => {
            const isProject = !task.ORDEN;
            const isRoot = !task.PRODUCTO && !task.ESPESOR && !task.COLOR;

            const projectId = buildProjectId(task.SEMANA, task.PRODUCTO, task.ESPESOR, task.COLOR);
            const taskId = isProject ? projectId : task.ORDEN + projectId;

            // Calcular parentId y nivel jerárquico
            let parentId = null;
            let taskLevel = 0;

            if (!isRoot) {
                if (isProject) {
                    const result = getParentIdWithLevel(task.SEMANA, task.PRODUCTO, task.ESPESOR, task.COLOR);
                    parentId = result.parentId;
                    taskLevel = result.level;
                } else {
                    // Para tareas (órdenes), el nivel es 4 (máximo)
                    parentId = projectId;
                    taskLevel = 4;
                }

                // Validación anti-cíclica
                if (parentId === taskId) {
                    console.warn(`Cyclic reference detected: ${taskId} -> ${parentId}`);
                    parentId = null;
                }

                // Logging para debugging de IDs inconsistentes
                if (import.meta.env.DEV && !isProject) {
                    const expectedSemana = task.SEMANA;
                    const parentSemana = parentId?.split("-")[0];
                    if (parentSemana && expectedSemana !== parentSemana) {
                        console.error(`❌ Mismatch SEMANA: Tarea ${taskId} (S${expectedSemana}) -> Parent ${parentId} (S${parentSemana})`, task);
                    }
                }
            }

            // Obtener fechas
            const taskStartDate = task.C2_DATPRI ? desdeMicrosiga(task.C2_DATPRI) : null;
            const projectDateRange = projectDateRanges.get(taskId);

            const baseTask = {
                id: taskId,
                text: task.C2_PRODUTO || projectId,
                panelColor: task.COLOR,
                panelColor2: task.COLOR2,
                productoTipo: task.PRODUCTO,
                producto: task.C2_PRODUTO,
                presupuesto: task.C2_USREQVE,
                semana: task.SEMANA,
                cliente: task.A1_NOME,
                espesor: task.ESPESOR,
                fEntrega: task.C6_ENTREG,
                orden: isProject ? "" : `${task.ORDEN}`,
                puertaSalida: task.C6_LOCAL,
                libCred: task.SIT_CRED2,
                libLog: task.SIT_LOG2,
                tiempoLinea: numberFormat(task.HORAS_PROD, true),
                cant: numberFormat(task.C2_QUANT, true),
                quje: numberFormat(task.C2_QUJE, true),
                filial: task.C2_FILIAL,
                prioridad: task.C2_PRIOR,
                progress: 0,
                order: index + 1,
                open: false,
                type: isProject ? "project" : "task",
                datos: task,
            };

            // Agregar parent si aplica
            if (!isRoot && parentId) {
                baseTask.parent = parentId;
            }

            // Asignar nivel y color basado en la jerarquía
            baseTask.taskLevel = taskLevel;
            baseTask.color = getPurpleColor(taskLevel);

            // Agregar duración y fecha para tareas
            if (!isProject) {
                baseTask.duration = 1;
                if (taskStartDate) {
                    baseTask.start_date = taskStartDate;
                }
            }

            // Agregar fechas calculadas para proyectos
            if (isProject && projectDateRange) {
                baseTask.start_date = projectDateRange.minDate;
                baseTask.end_date = projectDateRange.maxDate;
            }

            return baseTask;
        });

        // Logging para debugging (opcional - remover en producción)
        if (import.meta.env.DEV) {
            console.log(
                "\n📅 Project Date Ranges:",
                Array.from(projectDateRanges.entries()).map(([id, range]) => ({
                    projectId: id,
                    minDate: range.minDate instanceof Date ? range.minDate.toLocaleDateString() : range.minDate,
                    maxDate: range.maxDate instanceof Date ? range.maxDate.toLocaleDateString() : range.maxDate,
                })),
            );

            console.log(
                "\n📦 Tasks by Project:",
                Array.from(tasksByProject.entries()).map(([id, tasks]) => ({
                    projectId: id,
                    tareas: tasks,
                })),
            );

            // Buscar el proyecto específico que está causando problemas
            const problemaProyecto = "2-FR-05-PN5S";
            if (projectDateRanges.has(problemaProyecto)) {
                const range = projectDateRanges.get(problemaProyecto);
                const tareasAsignadas = tasksByProject.get(problemaProyecto) || [];
                console.log(`\n🎯 Detalle de ${problemaProyecto}:`, {
                    minDate: range.minDate,
                    maxDate: range.maxDate,
                    tareas: tareasAsignadas,
                });

                // Verificar si hay tareas con datos inconsistentes
                tareasAsignadas.forEach((tarea) => {
                    if (tarea.semana !== "2" || tarea.producto !== "FR") {
                        console.error(`❌ TAREA INCORRECTA en ${problemaProyecto}:`, tarea);
                    }
                });
            }

            console.log(`\n✅ Total de tareas únicas procesadas: ${taskSeenMap.size}`);
        }

        gantt.clearAll();
        gantt.parse({
            data: tasks,
            links: [],
        });

        const dateToStr = gantt.date.date_to_str(gantt.config.task_date);
        gantt.addMarker({
            start_date: new Date(),
            css: "today",
            text: "Ahora",
            title: dateToStr(new Date()),
        });
    } catch (error) {
        console.error("Error fetching tasks:", error);
    } finally {
        loading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use "sass:color";

// Page Container
.planificacion-page {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    padding: 1rem;
}

// Modern Header
.planificacion-header {
    background: linear-gradient(135deg, #5e35b1 0%, #4527a0 100%);
    padding: 8px 16px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(94, 53, 177, 0.2);
    animation: fadeIn 0.5s ease-in-out;

    .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 24px;

        .title-section {
            display: flex;
            align-items: center;
            color: white;

            .q-avatar {
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }

            .q-icon {
                opacity: 0.95;
            }

            .text-h4 {
                font-weight: 600;
                margin-bottom: 4px;
            }

            .text-subtitle2 {
                opacity: 0.9;
            }
        }

        .q-badge {
            font-size: 14px;
            padding: 8px 16px;
            border-radius: 20px;
            font-weight: 500;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        }
    }
}

// Modern Controls
.planificacion-controls {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .controls-toolbar {
        display: flex;
        justify-content: left;
        align-items: center;
        gap: 16px;
        padding: 16px;
        background: linear-gradient(to right, #f5f5f5, #fafafa);

        .toolbar-left {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;

            .machine-select,
            .date-input {
                min-width: 150px;
            }

            .action-btn {
                font-weight: 500;
                padding: 8px 20px;
                transition: all 0.2s ease;

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
                }
            }

            .zoom-controls {
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                border-radius: 8px;
                overflow: hidden;

                .zoom-btn {
                    transition: all 0.2s ease;

                    &:hover {
                        background: color.adjust(#9c27b0, $lightness: -5%);
                    }
                }
            }
        }

        .toolbar-right {
            .search-input {
                min-width: 300px;

                :deep(.q-field__control) {
                    border-radius: 8px;
                }

                :deep(.q-field__prepend) {
                    .q-icon {
                        color: #5e35b1;
                    }
                }
            }
        }
    }
}

// Gantt Chart Card
.planificacion-gantt-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    min-height: 0;

    .gantt-container-section {
        flex: 1;
        padding: 0;
        display: flex;
        flex-direction: column;
        min-height: 0;

        .gantt-chart {
            flex: 1;
            width: 100%;
            min-height: 0;
        }
    }

    // Custom Gantt Styles
    :deep(.gantt_grid_head_cell),
    :deep(.gantt_grid_head) {
        background: #fafafa !important;
        color: #424242 !important;
        font-weight: 600 !important;
        font-size: 13px !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        border-color: #e0e0e0 !important;
    }

    :deep(.gantt_row) {
        transition: background 0.2s ease;

        &:hover {
            background: #f5f9ff !important;
        }
    }

    :deep(.gantt_cell) {
        border-color: #f0f0f0 !important;
    }

    // Estilos base para barras de tareas
    :deep(.gantt_task_line) {
        border-radius: 6px;
        box-shadow: 0 2px 6px rgba(94, 53, 177, 0.3);
        transition: all 0.2s ease;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(94, 53, 177, 0.4);
        }
    }

    // Colores específicos por nivel jerárquico
    // Nivel 0: Semana (muy claro)
    :deep(.gantt_task_line.gantt_task_level_0) {
        --dhx-gantt-task-background: #e1bee7 !important;
        background: #e1bee7 !important;
        box-shadow: 0 2px 6px rgba(225, 190, 231, 0.4);

        &:hover {
            box-shadow: 0 4px 12px rgba(225, 190, 231, 0.5);
        }
    }

    // Nivel 1: Producto (claro)
    :deep(.gantt_task_line.gantt_task_level_1) {
        --dhx-gantt-task-background: #ce93d8 !important;
        background: #ce93d8 !important;
        box-shadow: 0 2px 6px rgba(206, 147, 216, 0.4);

        &:hover {
            box-shadow: 0 4px 12px rgba(206, 147, 216, 0.5);
        }
    }

    // Nivel 2: Espesor (medio)
    :deep(.gantt_task_line.gantt_task_level_2) {
        --dhx-gantt-task-background: #ba68c8 !important;
        background: #ba68c8 !important;
        box-shadow: 0 2px 6px rgba(186, 104, 200, 0.4);

        &:hover {
            box-shadow: 0 4px 12px rgba(186, 104, 200, 0.5);
        }
    }

    // Nivel 3: Color (oscuro)
    :deep(.gantt_task_line.gantt_task_level_3) {
        --dhx-gantt-task-background: #ab47bc !important;
        background: #ab47bc !important;
        box-shadow: 0 2px 6px rgba(171, 71, 188, 0.4);

        &:hover {
            box-shadow: 0 4px 12px rgba(171, 71, 188, 0.5);
        }
    }

    // Nivel 4: Orden (muy oscuro)
    :deep(.gantt_task_line.gantt_task_level_4) {
        --dhx-gantt-task-background: #4a148c !important;
        background: #4a148c !important;
        box-shadow: 0 2px 6px rgba(74, 20, 140, 0.5);

        &:hover {
            box-shadow: 0 4px 12px rgba(74, 20, 140, 0.6);
        }
    }

    :deep(.gantt_task_progress) {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 6px;
    }

    :deep(.gantt_grid_scale) {
        background: #fafafa !important;
        border-color: #e0e0e0 !important;
    }

    :deep(.gantt_tree_icon) {
        color: #5e35b1;
    }

    :deep(.gantt_side_content) {
        color: white;
        font-weight: 500;
    }

    :deep(.gantt_marker) {
        height: 100%;
        width: 3px;
        top: 0;
        position: absolute;
        text-align: center;
        background-color: var(--dhx-gantt-base-colors-error);
    }

    :deep(.gantt_marker .gantt_marker_content) {
        padding: 2px;
        background: inherit;
        color: var(--dhx-gantt-base-colors-white);
        position: absolute;
        font-size: var(--dhx-gantt-caption-font-size);
        line-height: var(--dhx-gantt-caption-line-height);
        font-weight: var(--dhx-gantt-caption-line-weight);
    }
}

// Animations
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// Responsive
@media (max-width: 1024px) {
    .planificacion-page {
        padding: 0.5rem;
    }

    .planificacion-header {
        .header-content {
            flex-direction: column;
            align-items: flex-start;
        }
    }

    .planificacion-controls {
        .controls-toolbar {
            flex-direction: column;
            align-items: stretch;

            .toolbar-left,
            .toolbar-right {
                width: 100%;
                justify-content: space-between;
            }

            .toolbar-right {
                .search-input {
                    min-width: 100%;
                }
            }
        }
    }
}
</style>
